import { useMutation } from '~/shared/hooks/promiseState'
import transform from 'lodash/transform'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'

import {
  bffCrmVerifyMember,
  userEmailSendVerification,
  userProviderPhoneVerify,
  userProviderPhoneVerifyCode,
} from '@fireflyhealth/api'

import { queueNotification } from '~/shared/notifications/actions'
import { patientsSlice } from '~/entities/person'
import { useAppDispatch } from '~/app/store/hooks'
import { logger } from '~/shared/lib/logger'

type PlainObject = Record<string, unknown>

/** The email-change modal's step, which these mutations advance as they succeed. */
type EmailState = { title: string; page: string }
type SetEmailState = (state: EmailState) => void
type SetError = (hasError: boolean) => void

/**
 * Deep diff between `object` and `base`: the keys of `object` whose values differ,
 * recursing into nested objects so only the changed leaves come back. Used to send
 * just the edited fields on a patient update.
 *
 * The result is typed `Partial<T>` because its keys are a subset of `object`'s. Note
 * that a nested value is itself a diff, so it can be a partial of the type declared
 * for that key rather than a whole one.
 */
export const difference = <T extends object>(object: T, base: object): Partial<T> => {
  return transform(object, (result: PlainObject, value, key) => {
    const baseValue = (base as PlainObject)[String(key)]
    if (!isEqual(value, baseValue)) {
      result[String(key)] =
        isObject(value) && isObject(baseValue) ? difference(value, baseValue) : value
    }
  }) as Partial<T>
}

const sendOtpForEmailUpdate = async (id: string) => {
  await userProviderPhoneVerifyCode.create({ data: { id: Number(id) } })
}

const verifyOtpForEmailUpdate = async (id: string, otp: string) => {
  await userProviderPhoneVerify.create({ data: { id: Number(id), code: otp } })
}

const triggerEmailVerification = async (id: number) => {
  await userEmailSendVerification.create({ id: String(id) })
}

export const useUpdateEmail = () => {
  const dispatch = useAppDispatch()
  return useMutation(
    async (patientId: number, email: string, setEmailState: SetEmailState, setError: SetError) => {
      try {
        if (email) {
          await dispatch(
            patientsSlice.thunks.updatePatient({
              id: patientId,
              email,
            }),
          )
          await triggerEmailVerification(patientId)
          dispatch(queueNotification({ message: 'Email has been updated', variant: 'success' }))
          setEmailState({ title: 'Ask Member to Follow Verification Link', page: 'success' })
          setError(false)
        }
      } catch (e) {
        if (e instanceof Error) {
          logger.error('[Patient] update email', e)
        }
        setError(true)
        dispatch(queueNotification({ message: 'Email update Failed.', variant: 'error' }))
      }
    },
  )
}

export const useOtpVerification = () => {
  const dispatch = useAppDispatch()
  return useMutation(
    async (
      patientId: string | null,
      otp: string,
      setEmailState: SetEmailState,
      setError: SetError,
    ) => {
      try {
        if (patientId && otp) {
          await verifyOtpForEmailUpdate(patientId, otp)
          dispatch(
            queueNotification({ message: 'OTP verification is successfull', variant: 'success' }),
          )
          setEmailState({ title: 'Ask Member for New Email Address', page: 'update_email' })
          setError(false)
        }
      } catch (e) {
        if (e instanceof Error) {
          logger.error('[Patient] verify one-time code', e)
        }
        setError(true)
        dispatch(queueNotification({ message: 'OTP verification failed', variant: 'error' }))
      }
    },
  )
}

export const useTriggerOtp = () => {
  const dispatch = useAppDispatch()
  return useMutation(
    async (patientId: string | null, setEmailState: SetEmailState, setError: SetError) => {
      try {
        if (patientId) {
          await sendOtpForEmailUpdate(patientId)
          dispatch(queueNotification({ message: 'OTP has been sent', variant: 'success' }))
          setEmailState({ title: 'Ask Member to Confirm Security Code', page: 'verify_otp' })
          setError(false)
        }
      } catch (e) {
        if (e instanceof Error) {
          logger.error('[Patient] send one-time code', e)
        }
        dispatch(queueNotification({ message: 'OTP Failed to send', variant: 'error' }))
      }
    },
  )
}

export const verifyMember = async (payload: {
  person_id: string
  member_identification_number: string
}) => {
  return (await bffCrmVerifyMember.create({ data: payload })).data
}
